import {
  Button as MUIButton,
  Dialog,
  DialogContent,
  FormControl,
  FormHelperText,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  Typography
} from "@material-ui/core";
import { ErrorOutline } from "@material-ui/icons";
import clsx from "clsx";
import { Form, Formik, FormikErrors } from "formik";
import { Html5QrcodeScanner } from "html5-qrcode";
import React, { useEffect, useState } from "react";
import { t } from "ttag";
import * as Yup from "yup";
import { TopBar } from "../../components";
import airwellQR from "../../images/airwellQR.png";
import HVACImg from "../../images/coolmaster.png";
import QRImg from "../../images/QR.png";
import { useStoreActions, useStoreState } from "../../models/RootStore";
import { ArrowBack } from "../../svgComponents";
import { registerNewDeviceStyle } from "./registerNewDevice.style";
import { getHostname } from "../../services/utils";
import DeviceBlockedDialog from "../../screens/SiteManagement/Device/DeviceBlockedDialog";
import { DeviceCredentialsForm } from "./DeviceCredentialsForm";
import { parseDeviceQrCode } from "./deviceQrCode";

const createConfig = (props: any) => {
  let config: any = {};
  if (props.fps) {
    config.fps = props.fps;
  }
  if (props.qrbox) {
    config.qrbox = props.qrbox;
  }
  if (props.aspectRatio) {
    config.aspectRatio = props.aspectRatio;
  }
  if (props.disableFlip !== undefined) {
    config.disableFlip = props.disableFlip;
  }
  return config;
};

const qrcodeRegionId = "html5qr-code-full-region";

interface RegisterDeviceFormValues {
  serial: string | null;
  pin: string | null;
  site: string | null;
  description?: string;
}

type RegisterDeviceFormErrors = FormikErrors<RegisterDeviceFormValues>;

const RegisterNewDevice: React.FC<any> = (props) => {
  const QRImage =
    getHostname().indexOf("airconnectpro") > -11 ? airwellQR : QRImg;

  const allCustomers = useStoreState((states) => states.customers.allCustomers);
  const { deviceRegistrationProgress = {} } = useStoreState((states) => states.devices);
  const { setDeviceRegistrationProgress } = useStoreActions((actions) => actions.devices);
  const [siteOptions, setSiteOptions] = useState<any>();
  const [selectedSite, setSelectedSite] = useState<any>(null);
  const [serial, setSerial] = useState<string>(deviceRegistrationProgress.serial || null);
  const [description, setDescription] = useState<string>(deviceRegistrationProgress.description || "");
  const [pin, setPin] = useState<string>(deviceRegistrationProgress.pin || null);
  const [confirmCheck, setConfirmCheck] = useState<boolean>(false);
  const [error, setError] = useState<any>(null);
  const [isDeviceBlocked, setIsDeviceBlocked] = useState<boolean>(false);
  const startLoader = useStoreActions((actions) => actions.loader.startLoader);
  const finishLoader = useStoreActions((actions) => actions.loader.finishLoader);
  const { addMessage } = useStoreActions((action) => action.errorMessage);
  const getSites = useStoreActions((actions) => actions.customers.getSites);
  const addNewDevice = useStoreActions((action) => action.createDevice);
  const getAccountByDeviceSerial = useStoreActions((actions) => actions.devices.getAccountByDeviceSerial);
  const [isCameraOpen, setIsCameraOpen] = useState<boolean>(false);
  const { customerId: selectedCustomer } = useStoreState((s) => s.selections.mobileSelections);
  const getCustomer = useStoreState((state) => state.customers.getCustomer);

  const {
    match: {
      params: {
        siteId,
        customerId = selectedCustomer || Object.keys(allCustomers || {})?.[0]
      }
    },
    history,
    history: { location: { search = "" } = {} } = {}
  } = props;

  const customer = getCustomer(customerId);
  const { canCreateSites } = customer?.permissions || {};
  const isLoggedIn = useStoreState((s) => s.isLoggedIn);
  const isSiteSelected = siteId && siteId !== "sites";

  const useStyles = makeStyles(registerNewDeviceStyle);
  const classes = useStyles();

  useEffect(() => {
    if (!search) {
      return;
    }
    const searchParams = new URLSearchParams(search);
    setSerial(searchParams.get("serial") || "");
    setPin(searchParams.get("pin") || "");
  }, [search]);

  useEffect(() => {
    if (!isCameraOpen) {
      return;
    }

    const config = createConfig(props);
    const verbose = props.verbose === true;
    const html5QrcodeScanner = new Html5QrcodeScanner(qrcodeRegionId, config, verbose);
    html5QrcodeScanner.render(onScanningQR, props.qrCodeErrorCallback);

    return () => {
      html5QrcodeScanner.clear().catch((error) => {
        console.error("Failed to clear html5QrcodeScanner. ", error);
      });
    };

  }, [isCameraOpen]);

  useEffect(() => {
    if (!isLoggedIn || !customerId) {
      return;
    }
    history.push(`/device-registration/${siteId || "sites"}/user-selection/${customerId}${search}`);
  }, [isLoggedIn, customerId, history, siteId, search]);

  useEffect(() => {
    if (!customerId || !isLoggedIn) {
      return;
    }

    startLoader();
    getSites({ customerId })
      .then((sites: any) => {
        const sitesArr = Object.values(sites);
        const sitesList = !!sitesArr.length ?
          sitesArr.map((site: any) => (
            <MenuItem value={site.id} key={site.id}>{site.name}</MenuItem>
          ))
          : <MenuItem key={`no-sites-option`} disabled value={""}>
            {t`No sites Options`}
          </MenuItem>;
        setSiteOptions(sitesList);
        setSelectedSite(isSiteSelected ? siteId : null);
      })
      .catch((error: any) => {
        addMessage({ message: error.message });
      })
      .finally(() => {
        finishLoader();
      });
  }, [
    addMessage,
    customerId,
    finishLoader,
    getSites,
    isLoggedIn,
    isSiteSelected,
    siteId,
    startLoader
  ]);
  const toggleCamera = () => {
    setIsCameraOpen(!isCameraOpen);
  };
  const onScanningQR = (qrCode: string | null) => {
    if (!qrCode) {
      return;
    }
    const { serial, pin } = parseDeviceQrCode(qrCode);

    setSerial(serial);
    setPin(pin);
    setSelectedSite(selectedSite || "");
    toggleCamera();
    if (!isLoggedIn) {
      handleSubmit({ serial, pin });
    }

  };
  const validationSchema = Yup.object({
    serial: Yup.string()
      .required(t`Required field`)
      .nullable(),
    pin: Yup.string()
      .required(t`Required field`)
      .nullable(),
    site: Yup.string()
      .required(t`Required field`)
      .nullable(),
    description: Yup.string()
  });

  const addDevice = (values: any) => {
    startLoader();
    const { serial, pin, site, description } = values;
    addNewDevice({
      site,
      serial: serial.replace(/\s+/g, " ").trim(),
      pin: pin.replace(/\s+/g, " ").trim(),
      description
    })
      .then((createdDevice: any) => {
        // const deviceId = createdDevice.id;
        history.push(`/dashboard`);
        // history.push(`/device/${deviceId}/system-detection`);
      })
      .catch((err: any) => {
        if (err.errorCode === "DEVICE_BLOCKED") {
          setIsDeviceBlocked(true);
          return;
        }
        addMessage({
          message: err.message
        });
      })
      .finally(() => {
        finishLoader();
      });
  };

  const handleCloseDeviceBlockedDialog = () => {
    setIsDeviceBlocked(false);
  };

  const handleSubmit = async (values: any) => {
    const { pin, serial } = values;

    const deviceInfo: any = await getAccountByDeviceSerial({
      serial: serial.replace(/\s+/g, " ").trim(),
      pin: pin.replace(/\s+/g, " ").trim()
    });

    if (!deviceInfo || !deviceInfo?.isFound) {
      setError(t`The serial you entered was not found. Please verify you entered it correctly`);
      return;
    }

    if (!deviceInfo?.isAuthenticated) {
      setError(t`The serial and pin do not match. Please verify you entered it correctly`);
      return;
    }

    if (!deviceInfo?.isConnected) {
      setError(t`The device is not connected. It must be connected to be registered. Please connect it and try again`);
      return;
    }

    if (deviceInfo?.isRegistered) {
      setError(t`The device is already registered to another account, and can not be registered again`);
      return;
    }

    if (isLoggedIn) {
      addDevice(values);
      return;
    }
    else {
      history.push("/");
    }
  };

  const goBackToSites = () => {
    setDeviceRegistrationProgress({});
    history.push(`/site-configuration?selectedSite=${siteId}`);
  };

  const handleConfirmToggle = () => {
    setConfirmCheck(!confirmCheck);
  };

  const handleCreateNewSite = () => {
    setDeviceRegistrationProgress({
      serial,
      pin,
      description
    });
    history.push(`/site-configuration/customer/${customerId}/add-site`);
  };

  /**
   * The serial / PIN / QR / location / confirm part of the form.
   *
   * Kept as a render function so the field handlers — which have to update both
   * the local state and Formik's — are named rather than inlined into the JSX.
   */
  const renderDeviceCredentialsForm = (
    values: RegisterDeviceFormValues,
    errors: RegisterDeviceFormErrors,
    setFieldValue: (field: string, value: unknown) => void
  ) => {
    const handleSerialChange = (value: string) => {
      setSerial(value);
      setFieldValue("serial", value);
    };

    const handlePinChange = (value: string) => {
      setPin(value);
      setFieldValue("pin", value);
    };

    const handleDescriptionChange = (value: string) => {
      setDescription(value);
      setFieldValue("description", value);
    };

    const isSubmitDisabled =
      !values?.pin?.length ||
      !values?.serial?.length ||
      !confirmCheck ||
      !values?.site?.length;

    return (
      <DeviceCredentialsForm
        values={values}
        errors={errors}
        onSerialChange={handleSerialChange}
        onPinChange={handlePinChange}
        onDescriptionChange={handleDescriptionChange}
        isCameraOpen={isCameraOpen}
        onToggleCamera={toggleCamera}
        qrCodeRegionId={qrcodeRegionId}
        qrImageSrc={QRImage}
        confirmChecked={confirmCheck}
        onConfirmToggle={handleConfirmToggle}
        isSubmitDisabled={isSubmitDisabled}
        extraFields={renderSitePicker(values, errors, setFieldValue)}
      />
    );
  };

  /**
   * Site picker, shown only in the authenticated flow — it needs a customer to
   * list sites for. Passed to DeviceCredentialsForm as its extra fields slot so
   * the public registration flow can render the same form without it.
   */
  const renderSitePicker = (
    values: RegisterDeviceFormValues,
    errors: RegisterDeviceFormErrors,
    setFieldValue: (field: string, value: unknown) => void
  ) => {
    if (!customerId) {
      return null;
    }

    const handleSiteChange = (event: React.ChangeEvent<{ value: unknown }>) => {
      setFieldValue("site", event.target.value);
    };

    return (
      <FormControl style={{ width: "100%" }}>
        {!values.site && (
          <InputLabel
            style={{ top: -8, left: 14 }}
            id="select-site-label"
          >{t`Select Site`}</InputLabel>
        )}
        <Select
          labelId="select-site-label"
          id="select-site"
          value={values.site}
          variant="outlined"
          className={classes.input}
          onChange={handleSiteChange}
          label={!values.site && t`Select Site`}
          error={values.site === ""}
          displayEmpty
          MenuProps={{
            classes: { paper: classes.menuItemsContainer },
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left"
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left"
            },
            getContentAnchorEl: null
          }}
        >
          {siteOptions}
        </Select>
        {values.site === "" && (
          <FormHelperText
            style={{ color: "#f44336", marginLeft: 14 }}
          >
            {typeof errors.site === "string" ? errors.site : t`Required field`}
          </FormHelperText>
        )}
        {canCreateSites && (
          <MUIButton
            onClick={handleCreateNewSite}
            style={{ textDecorationLine: "underline" }}
            color="primary"
          >
            OR Create a new site
          </MUIButton>
        )}
      </FormControl>
    );
  };

  return (
    <div className={classes.screenContainer}>
      {customerId && (
        <TopBar
          title={t`Connect Device`}
          leftIconComponent={<ArrowBack />}
          leftAction={goBackToSites}
          hideRightComponent={true}
        />

      )}
      <div className={classes.container}>
        <div className={classes.pageContent}>
          <div className={clsx(classes.header)}>
            <Typography className={classes.pageTitle}>
              {t`Lets connect your HVAC Device`}
            </Typography>
            <div className={classes.deviceImageContainer}>
              <div />
              <img src={HVACImg} alt={"HVAC"} className={classes.deviceImgStyle} />
            </div>
          </div>

          <div className={classes.formsWrapper}>
            <div className={classes.manualFormWrapper}>
              <Formik
                initialValues={{ serial, pin, site: selectedSite, description }}
                onSubmit={(values) => handleSubmit(values)}
                enableReinitialize={true}
                validationSchema={validationSchema}
                render={({ values, errors, setFieldValue, dirty, ...formikProps }) => {
                  return (
                    // @ts-ignore
                    <Form translate="yes">
                      {renderDeviceCredentialsForm(values, errors, setFieldValue)}
                    </Form>
                  );
                }}
              />
            </div>
          </div>

        </div>
      </div>

      <DeviceBlockedDialog open={isDeviceBlocked} onClose={handleCloseDeviceBlockedDialog} />

      {
        error &&
        <Dialog onClose={() => { }} open={error}>
          <div className={classes.errDialogHeader}>
            <Typography className={classes.errTitle}><ErrorOutline />{t`Device registration error`}</Typography>
          </div>
          <DialogContent className={classes.errContent}>
            <Typography>{error}</Typography>
            <MUIButton
              className={classes.errBtn}
              onClick={() => setError(null)}
              variant="contained"
              color="primary"
            >
              {t`Close`}
            </MUIButton>
          </DialogContent>
        </Dialog>
      }
    </div>
  );
};

export default RegisterNewDevice;
